import { useEffect, useState } from "react";
import { useRouter } from 'next/router';
// hooks
import useResponsive from "../../hooks/useResponsive";
// @mui
import {
  Avatar,
  Badge,
  Button,
  Card,
  Dialog,
  DialogContent,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
// components
import Iconify from '../../components/Iconify';
import SvgIconStyle from '../../components/SvgIconStyle';
// routes
import { PATH_JOBS } from '../../routes/paths';

// --------------------------------------------------

const todoContent = {
  "write": {
    "title": "작성 중인 공고가 있어요!",
    "desc": "작성을 마무리해주세요.",
    "icon": "icon-document-mono",
    "badge": false, // TODO: 배지는 기한이 24시간 이내일때만 보여주기
  },
  "suggest": {
    "title": "크루의 최종 제안을 확인해주세요!",
    "desc": "계약을 진행해 주세요.",
    "icon": "icon-user-check-mono",
    "badge": true,
  },
  "match": {
    "title": "크루와 매칭됐어요!",
    "desc": "프로필을 확인하고 미팅을 예약해주세요.",
    "icon": "ic_calendar",
    "badge": false,
  },
};

// --------------------------------------------------
export default function BizMatchingTodo({ jobPosts = [], addNewJobPost }) {
  const [openFullList, setOpenFullList] = useState(false);
  const [todoData, setTodoData] = useState([]);
  const matchingFilters = [
    "position-set",
    "method-direct",
    "detail-position-set",
    "context-set",
    "method-paste",
    "external-link-set",
    "task-set",
    "review-requested",
    "write-requested",
    "searching",
  ];
  const hasMatchingData = jobPosts?.filter((element) => matchingFilters.includes(element.status)).length > 0;
  const smUp = useResponsive("up", "sm");
  const router = useRouter();

  const initializeTodoData = async () => {
    const newTodoData = [];
    /**
     * 1. 작성중이거나 매칭중인 공고가 없다 = 새로운 공고 작성해라를 할일로 추가하기
     * 2. 작성중이거나 매칭중인 공고가 있다 = 공고/후보 상태마다 할일 추가하기
     */
    jobPosts?.forEach((element) => {
      if (["position-set",
        "method-direct", "detail-position-set", "context-set",
        "method-paste", "external-link-set"].includes(element.status)) {
          const todo = {
            step: "write",
            jobPost: element.id,
            candidate: null,
          }
          newTodoData.push(todo);
      }
      else if (element?.candidate_set?.length > 0) {
        element.candidate_set.forEach((candidate) => {
          const todo = {};
          switch (candidate.status) {
            case "offer-to-company":
            case "reselect-3way-time":
              todo.step = "match";
              todo.jobPost = element.id;
              todo.candidate = candidate.id;
              todo.desc = `${element?.detail_role?.card_display_name || element?.detail_role?.role || element?.get_position_display} | ${candidate?.crew?.nickname} 크루`;
              break;
            case "crew-negotiate-to-company":
              todo.step = "suggest";
              todo.jobPost = element.id;
              todo.candidate = candidate.id;
              todo.desc = `${element?.detail_role?.card_display_name || element?.detail_role?.role || element?.get_position_display} | ${candidate?.crew?.nickname} 크루`;
              break;
          };
          if (Object.keys(todo).length > 0) {
            newTodoData.push(todo);
          }
        });
      }
    });
    setTodoData(newTodoData);
  };

  useEffect(() => {
    if (jobPosts?.length > 0) {
      initializeTodoData();
    }
  }, [jobPosts]);

  const openFullListDialog = () => {
    setOpenFullList(true);
  };

  const todoAction = (todo)  => {
    const { jobPost, candidate } = todo;
    router.push({pathname: PATH_JOBS.root, query: {jid: jobPost, cid: candidate} });
  }
  

  return (
    <Card
      sx={{
        minWidth: smUp ? "390px" : 1,
        width: "100%",
        maxWidth: "100%",
        height: 1,
        borderRadius: 1.5,
        border: 1,
        borderColor: "rgba(145, 158, 171, 0.24)",
        boxShadow: "none",
        bgcolor: "rgba(0, 0, 0, 0.03)",
      }}
    >
      <Stack direction="row" aligntitems="center" justifyContent="space-between" sx={{ px: 3, pt: 2.5, mb: 0.5 }}>
        <Typography variant="subtitle2">
          해야할 일
        </Typography>
        {todoData?.length > 0 && (
        <Button
          variant="text"
          size="small"
          endIcon={<Iconify icon={'ic:chevron-right'} sx={{ ml: -1 }} />}
          sx={{
            color: "text.secondary",
            fontSize: "0.75rem",
            fontWeight: "normal",
          }}
          onClick={() => openFullListDialog()}
          disableFocusRipple
        >
          전체 보기 {todoData?.length}
        </Button>
        )}
      </Stack>
      {todoData?.length === 0 ? (
        hasMatchingData ? (
        <Stack sx={{ my: { xs: 2, md: 6 } }} alignItems={"center"}>
          <Iconify icon={'eva:info-fill'} sx={{ mb: 1, width: 32, height: 32, p: 1, bgcolor: "#00000014", color: "text.secondary", borderRadius: 2, fontSize: 16 }} />
          <Typography variant="overline">
            해야할 일이 없어요.
          </Typography>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            할 일이 생기면 알려드릴게요!
          </Typography>
        </Stack>
        ) : (
          <AddNewJobPostTodo addNewJobPost={addNewJobPost} />
        )
      ):(
        <Stack sx={{ px: 1.5, pb: 1.5 }}>
          {todoData.slice(0, 3).map((todo, index) => (
            <Stack key={index} direction="row" aligntitems="center" justifyContent="space-between"
              sx={{ 
                px: 1,
                py: 1.5,
                "&:hover": {
                  bgcolor: "rgba(145, 158, 171, 0.08)",
                  borderRadius: 1.5,
                  cursor: "pointer",
                }
              }}
              spacing={1.5}
              onClick={() => todoAction(todo)}
            >
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Badge
                  sx={{
                    width: 32,
                    height: 36,
                    "& .MuiBadge-badge": {
                      fontSize: "0.6rem",
                      width: 24,
                      height: 12,
                      right: 2,
                      top: 2,
                      padding: '2px 3px',
                    },
                  }}
                  badgeContent={"긴급"}
                  color="error"
                  anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  invisible={!todoContent[todo.step].badge}
                >
                  <Avatar sx={{ width: 32, height: 32, bgcolor: "rgba(0, 0, 0, 0.08)" }}>
                    <SvgIconStyle src={`/icons/${todoContent[todo.step].icon}.svg`} sx={{ width: 16, height: 16 }} />
                  </Avatar>
                </Badge>
                <Stack>
                  <Typography variant="overline">
                    {todoContent[todo.step].title}
                  </Typography>
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    {todo.desc || todoContent[todo.step].desc}
                  </Typography>
                </Stack>
              </Stack>
              <Stack justifyContent={"center"}>
                <Button
                  variant="contained"
                  size="small"
                  sx={{ fontSize: "0.75rem", boxShadow: "none" }}
                  disableFocusRipple
                >
                  확인하기
                </Button>
              </Stack>
            </Stack>
          ))}
        </Stack>
      )}
      <FullListDialog isOpen={openFullList} close={() => setOpenFullList(false)} todoList={todoData} todoAction={(todo) => todoAction(todo)} />
    </Card>
  );
}

function AddNewJobPostTodo({ addNewJobPost }) {
  return (
    <Stack direction="row" aligntitems="center" justifyContent="space-between"
      sx={{
        mx: 1.5,
        px: 1,
        py: 1.5,
        "&:hover": {
          bgcolor: "rgba(145, 158, 171, 0.08)",
          borderRadius: 1.5,
          cursor: "pointer",
        }
      }}
      spacing={1.5}
      onClick={() => addNewJobPost()}
    >
      <Stack direction="row" alignItems="center" spacing={1.5}>
        <Avatar sx={{ width: 32, height: 32, bgcolor: "rgba(0, 0, 0, 0.08)" }}>
          <SvgIconStyle src={`/icons/icon-pencil-mono.svg`} sx={{ width: 16, height: 16 }} />
        </Avatar>
        <Stack>
          <Typography variant="overline">
            새로운 공고를 작성해주세요!
          </Typography>
        </Stack>
      </Stack>
      <Stack justifyContent={"center"}>
        <Button
          variant="outlined"
          size="small"
          sx={{ fontSize: "0.75rem" }}
          disableFocusRipple
        >
          작성하기
        </Button>
      </Stack>
    </Stack>
  )
};

function FullListDialog({ isOpen, close, todoList = [], todoAction }) {
  const smUp = useResponsive('up', 'sm');
  return (
    <Dialog open={isOpen} onClose={close} fullWidth maxWidth="dg" fullScreen={!smUp}>
      <DialogContent>
        {!smUp && (
          <IconButton size="medium" color="primary" onClick={close} edge="start">
            <Iconify icon={'ic:chevron-left'} />
          </IconButton>
        )}
        <Stack direction="row" aligntitems="center" justifyContent="space-between" sx={{ pb: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="h5">
              해야할 일
            </Typography>
            <Typography variant="body1">
              {todoList.length}
            </Typography>
          </Stack>
          <IconButton size="medium" color="primary" onClick={close} sx={{ visibility: smUp ? "visible" : "hidden" }}>
            <Iconify icon={'eva:close-fill'} />
          </IconButton>
        </Stack>
        <Stack>
          {todoList.map((todo, index) => (
            <Stack key={index} direction="row" aligntitems="center" justifyContent="space-between"
              sx={{ 
                px: 1,
                py: 1.5,
                "&:hover": {
                  bgcolor: "rgba(145, 158, 171, 0.08)",
                  borderRadius: 1.5,
                  cursor: "pointer",
                }
              }}
              spacing={1.5}
              onClick={() => { close(); todoAction(todo) }}
            >
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Badge
                  sx={{
                    width: 32,
                    height: 36,
                    "& .MuiBadge-badge": {
                      fontSize: "0.6rem",
                      width: 24,
                      height: 12,
                      right: 2,
                      top: 2,
                      padding: '2px 3px',
                    },
                  }}
                  badgeContent={"긴급"}
                  color="error"
                  anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  invisible={!todoContent[todo.step].badge}
                >
                  <Avatar sx={{ width: 32, height: 32, bgcolor: "rgba(0, 0, 0, 0.08)" }}>
                    <SvgIconStyle src={`/icons/${todoContent[todo.step].icon}.svg`} sx={{ width: 16, height: 16 }} />
                  </Avatar>
                </Badge>
                <Stack>
                  <Typography variant="overline">
                    {todoContent[todo.step].title}
                  </Typography>
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    {todo.desc || todoContent[todo.step].desc}
                  </Typography>
                </Stack>
              </Stack>
              <Stack justifyContent={"center"}>
                <Button
                  variant="contained"
                  size="small"
                  sx={{ fontSize: "0.75rem", boxShadow: "none" }}
                  disableFocusRipple
                >
                  확인하기
                </Button>
              </Stack>
            </Stack>
          ))}
        </Stack>
      </DialogContent>
    </Dialog>
  );
};
